/**
 * Subscription Module
 * Handles Stripe subscription management and trial expiry blocking
 */

import { auth, db, functions } from './firebase.js';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

/**
 * Subscription State
 */
let subscriptionState = {
  membershipStatus: null,
  trialExpiry: null,
  currentPeriodEnd: null,
  isBlocked: false,
  subscriptionListener: null,
  // 'own' | 'linked' — tracks where membership comes from.
  // Kept for future use if linked users are charged separately.
  membershipSource: 'own'
};

// Re-render the membership badge in auth.js whenever subscription state updates.
// Dynamic import avoids a circular dependency at module load time.
function refreshMembershipBadge() {
  import('./auth.js').then(({ updateMembershipDisplay }) => updateMembershipDisplay()).catch(() => {});
}

/**
 * Initialize subscription management
 * Sets up real-time listener for membership status changes
 */
export function initializeSubscription(user) {
  if (!user) return;

  // Clean up existing listener if any
  if (subscriptionState.subscriptionListener) {
    console.log('🔄 Cleaning up existing subscription listener');
    subscriptionState.subscriptionListener();
    subscriptionState.subscriptionListener = null;
  }

  // Listen for membership changes in real-time
  const userRef = doc(db, 'users', user.uid);

  subscriptionState.subscriptionListener = onSnapshot(userRef, (snapshot) => {
    console.log('📊 Subscription snapshot received');
    if (snapshot.exists()) {
      const membership = snapshot.data().membership || {};

      console.log('💳 Membership status:', membership.status);

      subscriptionState.membershipStatus = membership.status;
      subscriptionState.trialExpiry = membership.trialExpiry;
      subscriptionState.currentPeriodEnd = membership.currentPeriodEnd || null;

      checkSubscriptionAccess(membership);
      refreshMembershipBadge();
    }
  });
}

/**
 * Switch subscription listener to the linked account owner's document.
 * Called when a user links to another account — they inherit the owner's membership.
 * To charge linked users separately in the future, add a check here before switching.
 */
export function switchSubscriptionToLinkedAccount(ownerId) {
  if (subscriptionState.subscriptionListener) {
    subscriptionState.subscriptionListener();
    subscriptionState.subscriptionListener = null;
  }

  subscriptionState.membershipSource = 'linked';

  const ownerRef = doc(db, 'users', ownerId);
  subscriptionState.subscriptionListener = onSnapshot(ownerRef, (snapshot) => {
    if (snapshot.exists()) {
      const membership = snapshot.data().membership || {};
      subscriptionState.membershipStatus = membership.status;
      subscriptionState.trialExpiry = membership.trialExpiry;
      subscriptionState.currentPeriodEnd = membership.currentPeriodEnd || null;
      checkSubscriptionAccess(membership);
      refreshMembershipBadge();
    }
  });
}

/**
 * Reset subscription listener back to the user's own document.
 * Called when a linked user unlinks from an owner account.
 */
export function resetSubscriptionToOwnAccount(userId) {
  if (subscriptionState.subscriptionListener) {
    subscriptionState.subscriptionListener();
    subscriptionState.subscriptionListener = null;
  }

  subscriptionState.membershipSource = 'own';

  const userRef = doc(db, 'users', userId);
  subscriptionState.subscriptionListener = onSnapshot(userRef, (snapshot) => {
    if (snapshot.exists()) {
      const membership = snapshot.data().membership || {};
      subscriptionState.membershipStatus = membership.status;
      subscriptionState.trialExpiry = membership.trialExpiry;
      subscriptionState.currentPeriodEnd = membership.currentPeriodEnd || null;
      checkSubscriptionAccess(membership);
      refreshMembershipBadge();
    }
  });
}

/**
 * Get the source of the current membership ('own' or 'linked')
 */
export function getMembershipSource() {
  return subscriptionState.membershipSource;
}

/**
 * Check if user has valid subscription access
 * Shows blocking modal if trial expired
 */
export function checkSubscriptionAccess(membership) {
  const now = new Date();
  const status = membership.status;

  // Allow access if premium or active trial
  if (status === 'premium') {
    subscriptionState.isBlocked = false;
    hideBlockingModal();
    return;
  }

  // Check if trial is expired
  if (status === 'trial' && membership.trialExpiry) {
    const expiryDate = typeof membership.trialExpiry.toDate === 'function'
      ? membership.trialExpiry.toDate()
      : new Date(membership.trialExpiry);

    if (now > expiryDate) {
      subscriptionState.isBlocked = true;
      showBlockingModal('expired');
    } else {
      subscriptionState.isBlocked = false;
      hideBlockingModal();
    }
  } else if (status === 'expired') {
    subscriptionState.isBlocked = true;
    showBlockingModal('expired');
  } else if (status === 'cancelled') {
    subscriptionState.isBlocked = true;
    showBlockingModal('cancelled');
  } else if (status === 'past_due') {
    subscriptionState.isBlocked = true;
    showBlockingModal('past_due');
  }
}

// Track if modal is already configured to prevent duplicate updates
let modalConfiguredForStatus = null;

/**
 * Show full-screen blocking modal requiring upgrade
 * Updates content based on subscription status
 */
function showBlockingModal(status = 'expired') {
  console.log(`🚨 showBlockingModal called with status: ${status}`);

  // Skip if already configured for this status to prevent flashing
  if (modalConfiguredForStatus === status) {
    console.log('⏭️ Modal already configured for this status, skipping update');
    return;
  }

  const modal = document.getElementById('trial-expired-modal');
  if (!modal) {
    console.error('❌ Modal element not found');
    return;
  }

  // Get all content elements
  const content = modal.querySelector('.trial-expired-content');
  const icon = modal.querySelector('.trial-expired-icon');
  const title = modal.querySelector('h2');
  const mainText = modal.querySelector('p:first-of-type');
  const subtitle = modal.querySelector('.trial-expired-subtitle');
  const features = modal.querySelector('.trial-expired-features');
  const pricing = modal.querySelector('.trial-expired-price');
  const button = modal.querySelector('.trial-expired-upgrade-btn');
  const footer = modal.querySelector('.trial-expired-footer');

  console.log('📝 Elements found:', {
    icon: !!icon,
    title: !!title,
    mainText: !!mainText,
    subtitle: !!subtitle,
    features: !!features,
    pricing: !!pricing,
    button: !!button,
    footer: !!footer
  });

  if (status === 'cancelled') {
    if (icon) icon.textContent = '💔';
    if (title) title.textContent = 'Subscription Cancelled';
    if (mainText) {
      mainText.textContent = 'Your subscription has been cancelled and your access has ended.';
      mainText.style.display = 'block';
    }
    if (subtitle) {
      subtitle.style.display = 'block';
      subtitle.textContent = 'Resubscribe anytime to pick up where you left off:';
    }
    if (features) features.style.display = 'block';
    if (pricing) pricing.style.display = 'block';
    if (footer) footer.style.display = 'block';
    if (button) {
      button.textContent = 'Resubscribe — $4.99/month';
      button.onclick = () => redirectToCheckout();
    }
  } else if (status === 'past_due') {
    if (icon) icon.textContent = '💳';
    if (title) title.textContent = 'Payment Issue';
    if (mainText) {
      mainText.textContent = 'Your payment method failed. Please update your billing information to continue using Budgley.';
      mainText.style.display = 'block';
    }
    if (subtitle) subtitle.style.display = 'none';
    if (features) features.style.display = 'none';
    if (pricing) pricing.style.display = 'none';
    if (footer) footer.style.display = 'none';
    if (button) {
      button.textContent = 'Update Payment Method';
      button.onclick = () => window.openBillingPortal();
    }
  } else {
    // Trial expired or subscription expired
    if (icon) icon.textContent = '⏰';
    if (title) title.textContent = 'Your Trial Has Ended';
    if (mainText) {
      mainText.textContent = 'Thanks for trying Budgley! Your 30-day trial has expired.';
      mainText.style.display = 'block';
    }
    if (subtitle) {
      subtitle.style.display = 'block';
      subtitle.textContent = 'Subscribe to continue using all features:';
    }
    if (features) features.style.display = 'block';
    if (pricing) pricing.style.display = 'block';
    if (footer) footer.style.display = 'block';
    if (button) {
      button.textContent = 'Subscribe — $4.99/month';
      button.onclick = () => redirectToCheckout();
    }
  }

  // Mark as configured for this status
  modalConfiguredForStatus = status;

  console.log('✅ Modal content updated, making visible...');

  // Save current scroll position
  const scrollY = window.scrollY;

  // Scroll to top to ensure modal is visible
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;

  // Lock body scroll by fixing position at current scroll
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.width = '100%';
  document.body.style.overflow = 'hidden';

  // Store scroll position for restoration
  modal.dataset.scrollY = scrollY;

  modal.classList.add('active');
  console.log('✅ Modal should now be visible and scroll locked');
}

/**
 * Hide blocking modal
 */
function hideBlockingModal() {
  console.log('👻 hideBlockingModal called');
  const modal = document.getElementById('trial-expired-modal');
  if (modal && modal.classList.contains('active')) {
    // Get saved scroll position
    const scrollY = modal.dataset.scrollY || '0';

    // Unlock body scroll
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';

    // Restore scroll position
    window.scrollTo(0, parseInt(scrollY, 10));

    modal.classList.remove('active');
    modalConfiguredForStatus = null; // Reset so it can be reconfigured when shown again
    console.log('✅ Modal hidden and scroll restored');
  }
}

/**
 * Redirect user to Stripe Checkout
 */
export async function redirectToCheckout(buttonEl) {
  const button = buttonEl || document.querySelector('.trial-expired-upgrade-btn');
  const originalText = button?.textContent;

  if (button) {
    button.disabled = true;
    button.innerHTML = '<span class="btn-spinner"></span>Redirecting to checkout...';
  }

  try {
    console.log('🚀 Starting checkout process...');

    const user = auth.currentUser;
    if (!user) {
      throw new Error('Must be authenticated');
    }

    const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
    const result = await createCheckoutSession({
      returnUrl: window.location.origin
    });

    if (result.data.url) {
      window.location.href = result.data.url;
    } else {
      throw new Error('No checkout URL returned');
    }

  } catch (error) {
    console.error('❌ Error redirecting to checkout:', error);
    if (button) {
      button.disabled = false;
      button.textContent = originalText;
    }
    alert('Failed to start checkout. Please try again.');
  }
}

/**
 * Open Stripe Customer Portal for subscription management
 */
export async function openBillingPortal(buttonEl) {
  const button = buttonEl instanceof HTMLElement ? buttonEl : null;
  const originalText = button?.textContent;

  if (button) {
    button.disabled = true;
    button.innerHTML = '<span class="btn-spinner"></span>Opening portal...';
  }

  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Must be authenticated');
    }

    // Call Cloud Function via Firebase Hosting rewrite
    const createPortalSession = httpsCallable(functions, 'createPortalSession');

    const result = await createPortalSession({
      returnUrl: `${window.location.origin}/settings`
    });

    // Open portal in new window
    if (result.data.url) {
      window.open(result.data.url, '_blank');
    } else {
      throw new Error('No portal URL returned');
    }

  } catch (error) {
    console.error('Error opening billing portal:', error);
    alert('Failed to open billing portal. Please try again.');
  } finally {
    // Always restore — portal opens in a new tab so this page stays active
    if (button) {
      button.disabled = false;
      button.textContent = originalText;
    }
  }
}

/**
 * Get current membership status
 */
export function getMembershipStatus() {
  return subscriptionState.membershipStatus;
}

/**
 * Get the full membership data object from the active listener (own or linked owner).
 * Use this instead of reading Firestore directly so linked users see the owner's membership.
 */
export function getMembershipData() {
  return {
    status: subscriptionState.membershipStatus,
    trialExpiry: subscriptionState.trialExpiry,
    currentPeriodEnd: subscriptionState.currentPeriodEnd
  };
}

/**
 * Check if user is currently blocked
 */
export function isUserBlocked() {
  return subscriptionState.isBlocked;
}

/**
 * Get days remaining in trial
 */
export function getDaysRemainingInTrial() {
  if (!subscriptionState.trialExpiry) return 0;

  const now = new Date();
  const expiryDate = typeof subscriptionState.trialExpiry.toDate === 'function'
    ? subscriptionState.trialExpiry.toDate()
    : new Date(subscriptionState.trialExpiry);
  const diffTime = expiryDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
}

/**
 * Cleanup subscription listener
 */
export function cleanupSubscription() {
  if (subscriptionState.subscriptionListener) {
    subscriptionState.subscriptionListener();
    subscriptionState.subscriptionListener = null;
  }
}

/**
 * Handle successful checkout return
 */
export function handleCheckoutSuccess() {
  const urlParams = new URLSearchParams(window.location.search);
  const success = urlParams.get('success');
  const sessionId = urlParams.get('session_id');

  if (success === 'true' && sessionId) {
    let message = 'Welcome to Budgley Premium!';

    const trialExpiry = subscriptionState.trialExpiry;
    if (trialExpiry) {
      const expiryDate = typeof trialExpiry.toDate === 'function'
        ? trialExpiry.toDate()
        : new Date(trialExpiry);
      const daysLeft = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));
      if (daysLeft > 0) {
        const chargeDate = expiryDate.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric', year: 'numeric' });
        message += ` You have ${daysLeft} more day${daysLeft === 1 ? '' : 's'} of your free trial. Subscription charges start on ${chargeDate}.`;
      }
    }

    showSuccessMessage(message);
    window.history.replaceState({}, document.title, window.location.pathname);
  }
}

/**
 * Handle canceled checkout
 */
export function handleCheckoutCanceled() {
  const urlParams = new URLSearchParams(window.location.search);
  const canceled = urlParams.get('canceled');

  if (canceled === 'true') {
    window.history.replaceState({}, document.title, window.location.pathname);
  }
}

/**
 * Show success message
 */
function showSuccessMessage(message) {
  // Create temporary success notification
  const notification = document.createElement('div');
  notification.className = 'subscription-success-notification';
  notification.textContent = message;
  document.body.appendChild(notification);

  // Auto-remove after 5 seconds
  setTimeout(() => {
    notification.classList.add('fade-out');
    setTimeout(() => notification.remove(), 300);
  }, 5000);
}
